import { BrowserZmlClient, IFile, IFileType, IFileTypeMap, IPublicLink } from "@zappar/zw-api-client";
import { BaseAsset } from "../../base";
import { MediaType } from "../../../types";

export class ZapworksAsset<T extends IFileType> extends BaseAsset {
  protected _client: BrowserZmlClient;
  protected file: IFileTypeMap[T];
  fileType: T;

  constructor(apiClient: BrowserZmlClient, file: IFile) {
    super();
    this._client = apiClient;
    this.file = file as IFileTypeMap[T];
    this.fileType = file.type as T;
  }

  getZmlFile(): IFileTypeMap[T] {
    return this.file;
  }

  getId(): string {
    return this.file.id;
  }

  getName(): string {
    return this.file.caption || 'Untitled';
  }

  async setName(newName: string): Promise<void> {
    const result = await this._client.updateFile(this.file.id, { caption: newName }, this.file.workspaceId || undefined);
    this.file.caption = result.caption;
  }

  async setServiceNowTicket(ticket: string | null): Promise<void> {
    const result = await this._client.updateFile(this.file.id, { meta: { serviceNowTicket: ticket } }, this.file.workspaceId || undefined);
    this.file.meta = { ...this.file.meta, serviceNowTicket: result.meta?.serviceNowTicket ?? null };
  }

  getMediaType(): MediaType {
    return MediaType.Unknown
  }

  getVersion(): number {
    return this.file.version;
  }

  async getImageUrlForCard(size?: { width: number; height?: number }, useCache = true): Promise<string> {
    // TODO: Implement proper thumbnail URL generation
    // For now, just return the download URL
    return this.getFileUrl(useCache);
  }

  async getPreviewUrl(size?: { width: number; height?: number }, useCache = true): Promise<string> {
    return this.getImageUrlForCard(size, useCache);
  }

  /**
   * Default download URL is the raw JWT-signed CDN URL of the file root.
   * Per-type subclasses (`ImageAsset`, `Model3DAsset`, etc.) override this to
   * append the appropriate public filename suffix (`/img`, `/model.glb`, …).
   */
  async getDownloadUrl(useCache = true): Promise<string> {
    return this.getFileUrl(useCache);
  }

  /**
   * Fetches the current file as a Blob plus the server-supplied filename.
   * Streams the bytes — call lazily on user action.
   */
  async downloadFile(): Promise<{ blob: Blob, filename: string }> {
    return this._client.downloadFile(this.file.id, this.file.workspaceId || undefined);
  }

  /**
   * Fetches a historical version of the file as a Blob plus the server-supplied
   * filename. Streams the bytes — call lazily on user action.
   */
  async downloadFileVersion(versionId: string): Promise<{ blob: Blob, filename: string }> {
    return this._client.downloadFileVersion(this.file.id, versionId, this.file.workspaceId || undefined);
  }

  /**
   * Provides a fresh URL with a new JWT token without re-fetching all file metadata.
   * @returns Promise with the new URL containing a fresh JWT token
   */
  getFileUrl(useCache = true): Promise<string> {
    return this._client.getFileUrl(this.file.id, useCache, this.file.workspaceId || undefined);
  }

  getWorkspaceId(): string | undefined {
    return this.file.workspaceId || undefined;
  }

  getSource(): string {
    return this.file.source || '';
  }

  async getPublicLink(): Promise<IPublicLink | null> {
    if (this.file.workspaceId) {
      try {
        return await this._client.getPublicLink(this.file.workspaceId, this.file.id);
      } catch {
        return null;
      }
    }
    return null;
  }

  getMetadata() {
    return {
      status: this.file.status,
      date: this.file.date,
      size: this.file.size,
      uploadedBy: this.file.uploadedBy,
      folder: this.file.folderId,
      labels: this.file.labels,
      version: this.file.version,
      workflowStatusId: this.file.workflowStatusId ?? null,
      workspaceId: this.file.workspaceId,
      binned: this.file.binned,
      canDelete: this.file.canDelete,
      canRestore: this.file.canRestore,
      source: this.file.source,
      access: this.file.access,
      serviceNowTicket: ((this.file.meta as Record<string, unknown>)?.serviceNowTicket as string | null) ?? null,
    };
  }
}

